
interface BioGeneratorProps {
  name: string | null;
  profession: string | null;
  skills: string[];
  experience: number;
  education: string;
  languages: string[];
  location: string;
}

// Helper to map experience number to experience level label
const EXPERIENCE_LEVELS = [
  { min: 0, max: 0, label: 'less than 1 year' },
  { min: 1, max: 2, label: '1-2 years' },
  { min: 3, max: 5, label: '3-5 years' },
  { min: 6, max: 10, label: '6-10 years' },
  { min: 11, max: 15, label: '11-15 years' },
  { min: 16, max: 20, label: '16-20 years' },
  { min: 21, max: 100, label: 'more than 20 years' },
];

function getExperienceLevelLabel(experience: number): string {
  for (const level of EXPERIENCE_LEVELS) {
    if (experience >= level.min && experience <= level.max) {
      return level.label;
    }
  }
  return '';
}

// Generate a bio based on the user's profile information
export async function generateBio({
  name,
  profession,
  skills,
  experience,
  education,
  languages,
  location
}: BioGeneratorProps): Promise<string> {
  try {
    // Validate required fields
    if (!profession || experience === undefined || experience === null || experience < 0) {
      return 'Profile information is incomplete. Please provide your profession and experience.';
    }

    // Use the experience level label for more natural language
    const experienceLevelLabel = getExperienceLevelLabel(experience);
    const skillsList = skills && skills.length > 0 
      ? `specializing in ${skills.slice(0, 3).join(', ')}${skills.length > 3 ? ', and more' : ''}`
      : '';
    const educationText = getEducationText(education);
    const languagesText = languages && languages.length > 0 
      ? `I am fluent in ${languages.join(', ')}.` 
      : '';
    const locationText = location ? `Based in ${location}, ` : '';

    // Prevent confusing or inaccurate descriptions
    if (!experienceLevelLabel) {
      return 'Please select a valid experience range to generate your bio.';
    }

    const templates = [
      `${locationText}I am a passionate ${profession} ${skillsList} with ${experienceLevelLabel} of experience. ${educationText} ${languagesText} I'm constantly looking to push creative boundaries and deliver exceptional design solutions.`,
      `A dedicated ${profession} ${skillsList} with ${experienceLevelLabel} in the field. ${locationText}${educationText} ${languagesText} I combine technical expertise with creative vision to create compelling designs.`,
      `${locationText}I bring ${experienceLevelLabel} of experience as a ${profession} ${skillsList}. ${educationText} ${languagesText} My goal is to create meaningful designs that solve real problems while delighting users.`,
      `Creative ${profession} with ${experienceLevelLabel} of professional experience ${skillsList}. ${locationText}${educationText} ${languagesText} I approach each project with fresh perspective and attention to detail.`,
      `${locationText}${profession} with ${experienceLevelLabel} of experience ${skillsList}. ${educationText} ${languagesText} I'm passionate about creating innovative solutions that elevate brands and enhance user experiences.`
    ];

    // Select a random template
    const selectedTemplate = templates[Math.floor(Math.random() * templates.length)];

    // Clean up extra spaces
    return selectedTemplate.replace(/\s+/g, ' ').trim();
  } catch (error) {
    console.error('Error generating bio:', error);
    return 'An experienced designer with a passion for creating innovative solutions.';
  }
}

// Helper functions to format text
function getEducationText(educationCode: string): string {
  const educationMap: Record<string, string> = {
    'high_school': 'I have completed high school education.',
    'bachelors': 'I hold a Bachelor\'s degree in design.',
    'mba': 'I have an MBA in International Business Management.',
    'masters': 'I hold a Master\'s degree in design.',
    'doctorate': 'I hold a Doctorate degree in my field.'
  };
  
  return educationMap[educationCode] || '';
}
